import { useEffect, useRef, useState, type DragEvent, type ReactNode } from 'react'
import { AlertCircle, FileSpreadsheet, FileText, Loader2, Paperclip, X } from 'lucide-react'
import { apiClient } from '@/api/client'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { formatBytes } from '@/lib/aiAttachments'
import { cn } from '@/lib/utils'
import type { ChatAttachment } from './chatRows'
import type { ComposerFile } from './useCopilotChat'

// ── Bhookly AI: attached files (F-AI-62) ────────────────────────────────────
// The composer's chips while files upload, the files shown in a sent message,
// and the drop zone around a chat surface.

// Files fetched for this session, by id: a restored chat shows its images
// without fetching them again on every render.
const fileUrls = new Map<string, Promise<string>>()

function attachmentUrl(id: string): Promise<string> {
  let url = fileUrls.get(id)
  if (!url) {
    url = apiClient.aiGetAttachmentFile(id).then((blob) => URL.createObjectURL(blob))
    url.catch(() => fileUrls.delete(id))
    fileUrls.set(id, url)
  }
  return url
}

/** The image's URL: this device's copy, else the server's (fetched with the login). */
function useAttachmentUrl(a: ChatAttachment, enabled: boolean): string | null {
  const [url, setUrl] = useState<string | null>(a.localUrl ?? null)
  useEffect(() => {
    if (a.localUrl || !enabled) return
    let live = true
    attachmentUrl(a.id)
      .then((u) => live && setUrl(u))
      .catch(() => {})
    return () => {
      live = false
    }
  }, [a.id, a.localUrl, enabled])
  return url
}

function FileIcon({ name, className }: { name: string; className?: string }) {
  const sheet = /\.(xlsx|xlsm|csv|tsv)$/i.test(name)
  const Icon = sheet ? FileSpreadsheet : FileText
  return <Icon className={cn('h-4 w-4 shrink-0', sheet ? 'text-emerald-600' : 'text-rose-600', className)} aria-hidden />
}

function fileDetail(kind: ChatAttachment['kind'], sizeBytes: number, pages?: number): string {
  const size = formatBytes(sizeBytes)
  if (kind === 'pdf' && pages) return `PDF · ${pages} page${pages === 1 ? '' : 's'} · ${size}`
  return size
}

/** Saves one of the staff member's files (works in the desktop app too, unlike a new tab). */
async function saveFile(a: ChatAttachment) {
  try {
    const href = a.localUrl ?? (await attachmentUrl(a.id))
    const link = document.createElement('a')
    link.href = href
    link.download = a.name
    link.click()
  } catch {
    /* gone (another device removed it): nothing to save */
  }
}

// ── In the composer ─────────────────────────────────────────────────────────

export function ComposerFileChips({ files, onRemove }: { files: ComposerFile[]; onRemove: (localId: string) => void }) {
  if (!files.length) return null
  return (
    <ul className="mb-2 flex flex-wrap gap-2" aria-label="Attached files">
      {files.map((f) => (
        <li
          key={f.localId}
          className={cn(
            'relative flex max-w-[220px] items-center gap-2 rounded-lg border bg-background p-1.5 pr-7 text-xs',
            f.status === 'error' ? 'border-destructive/50' : 'border-border',
          )}
          title={f.error ?? f.name}
        >
          {f.kind === 'image' && f.previewUrl ? (
            <img src={f.previewUrl} alt="" className="h-9 w-9 shrink-0 rounded-md object-cover" />
          ) : (
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted">
              <FileIcon name={f.name} />
            </span>
          )}
          <span className="min-w-0">
            <span className="block truncate font-medium text-foreground">{f.name}</span>
            <span
              className={cn(
                'flex items-center gap-1 truncate text-[11px]',
                f.status === 'error' ? 'text-destructive' : 'text-muted-foreground',
              )}
            >
              {f.status === 'uploading' ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                  {f.progress > 0 && f.progress < 1 ? `${Math.round(f.progress * 100)}%` : 'Uploading…'}
                </>
              ) : f.status === 'error' ? (
                <>
                  <AlertCircle className="h-3 w-3 shrink-0" aria-hidden />
                  <span className="truncate">{f.error}</span>
                </>
              ) : (
                fileDetail(f.kind, f.size, f.pages)
              )}
            </span>
          </span>
          <button
            type="button"
            onClick={() => onRemove(f.localId)}
            aria-label={`Remove ${f.name}`}
            className="absolute right-1 top-1 rounded-md p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </li>
      ))}
    </ul>
  )
}

export function AttachButton({ accept, disabled, onPick }: { accept: string; disabled?: boolean; onPick: (files: File[]) => void }) {
  const inputRef = useRef<HTMLInputElement>(null)
  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        aria-label="Attach files"
        title="Attach a screenshot, photo, PDF, Excel or Word file"
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40"
      >
        <Paperclip className="h-4 w-4" aria-hidden />
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        className="hidden"
        onChange={(e) => {
          onPick(Array.from(e.target.files ?? []))
          e.target.value = '' // the same file can be picked again
        }}
      />
    </>
  )
}

// ── In a sent message ───────────────────────────────────────────────────────

function ImageThumb({ a, onOpen }: { a: ChatAttachment; onOpen: (url: string) => void }) {
  const url = useAttachmentUrl(a, true)
  return (
    <button
      type="button"
      onClick={() => url && onOpen(url)}
      aria-label={`View ${a.name}`}
      className="overflow-hidden rounded-xl border border-border bg-muted"
    >
      {url ? (
        <img src={url} alt={a.name} className="block max-h-40 max-w-[220px] object-cover" />
      ) : (
        <span className="flex h-24 w-32 items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />
        </span>
      )}
    </button>
  )
}

export function MessageAttachments({ attachments }: { attachments: ChatAttachment[] }) {
  const [viewing, setViewing] = useState<{ url: string; name: string } | null>(null)
  return (
    <div className="flex max-w-[85%] flex-wrap justify-end gap-2">
      {attachments.map((a) =>
        a.kind === 'image' ? (
          <ImageThumb key={a.id} a={a} onOpen={(url) => setViewing({ url, name: a.name })} />
        ) : (
          <button
            type="button"
            key={a.id}
            onClick={() => void saveFile(a)}
            title={`Save ${a.name}`}
            className="flex max-w-[240px] items-center gap-2 rounded-xl border border-border bg-background px-3 py-2 text-left text-xs hover:bg-muted"
          >
            <FileIcon name={a.name} />
            <span className="min-w-0">
              <span className="block truncate font-medium text-foreground">{a.name}</span>
              <span className="block text-[11px] text-muted-foreground">{fileDetail(a.kind, a.sizeBytes, a.pages)}</span>
            </span>
          </button>
        ),
      )}
      <Dialog open={!!viewing} onOpenChange={(open) => !open && setViewing(null)}>
        <DialogContent className="max-w-3xl p-3">
          <DialogTitle className="truncate pr-8 text-sm">{viewing?.name}</DialogTitle>
          {viewing && <img src={viewing.url} alt={viewing.name} className="max-h-[75vh] w-full rounded-lg object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ── Drop zone ───────────────────────────────────────────────────────────────

function hasFiles(e: DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes('Files')
}

/**
 * Drop handlers for a chat surface (spread on its root, which must be
 * `relative`) and the overlay shown while files are dragged over it.
 */
export function useFileDrop(enabled: boolean, onFiles: (files: File[]) => void) {
  const [over, setOver] = useState(false)
  const depth = useRef(0)
  const props = enabled
    ? {
        onDragEnter: (e: DragEvent) => {
          if (!hasFiles(e)) return
          e.preventDefault()
          depth.current += 1
          setOver(true)
        },
        onDragOver: (e: DragEvent) => {
          if (hasFiles(e)) e.preventDefault()
        },
        onDragLeave: () => {
          depth.current = Math.max(0, depth.current - 1)
          if (depth.current === 0) setOver(false)
        },
        onDrop: (e: DragEvent) => {
          if (!hasFiles(e)) return
          e.preventDefault()
          depth.current = 0
          setOver(false)
          onFiles(Array.from(e.dataTransfer.files))
        },
      }
    : {}
  const overlay: ReactNode =
    enabled && over ? (
      <div className="pointer-events-none absolute inset-2 z-20 flex flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-primary bg-background/90 text-sm">
        <Paperclip className="h-5 w-5 text-primary" aria-hidden />
        <span className="font-medium text-foreground">Drop files to attach</span>
        <span className="text-xs text-muted-foreground">Screenshots, photos, PDFs, Excel or Word files</span>
      </div>
    ) : null
  return { props, overlay }
}
